"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SMALL,
  HINT,
  LABEL,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  BLOOM_LEVELS,
  type BloomLevel,
  buildObjective,
  findVagueTerms,
  type ObjectiveOrder,
} from "@/lib/tool-kit/classroom/objectives";

/** English strings. A translated content file overrides these through `ui`. */
export const LEARNING_OBJECTIVE_GENERATOR_STRINGS = {
  audienceLabel: "A — Audience: who is learning",
  audiencePlaceholder: "grade 7 students",
  audienceHint:
    'Write it as it reads mid-sentence. Left empty, it says "students".',
  audienceFallback: "students",
  levelLabel: "Bloom's level (revised taxonomy)",
  levelNames: {
    remember: "Remember",
    understand: "Understand",
    apply: "Apply",
    analyze: "Analyze",
    evaluate: "Evaluate",
    create: "Create",
  },
  levelDescriptions: {
    remember: "Retrieve facts, terms and basic concepts from memory.",
    understand:
      "Construct meaning from what was taught — explain, classify, summarize, compare.",
    apply: "Carry out or use a procedure in a given situation.",
    analyze:
      "Break material into parts and work out how the parts relate to each other and the whole.",
    evaluate: "Make judgments based on criteria and standards.",
    create:
      "Put elements together into a coherent whole or an original product.",
  },
  levelVerbs: {
    remember: [
      "define",
      "identify",
      "label",
      "list",
      "match",
      "name",
      "recall",
      "recognize",
      "reproduce",
      "state",
    ],
    understand: [
      "classify",
      "compare",
      "describe",
      "explain",
      "give examples of",
      "infer",
      "interpret",
      "paraphrase",
      "predict",
      "summarize",
    ],
    apply: [
      "apply",
      "calculate",
      "carry out",
      "demonstrate",
      "execute",
      "implement",
      "solve",
      "use",
    ],
    analyze: [
      "analyze",
      "attribute",
      "differentiate",
      "distinguish",
      "examine",
      "organize",
      "outline",
      "deconstruct",
    ],
    evaluate: [
      "appraise",
      "assess",
      "check",
      "critique",
      "defend",
      "judge",
      "justify",
      "test",
    ],
    create: [
      "compose",
      "construct",
      "design",
      "develop",
      "formulate",
      "generate",
      "hypothesize",
      "plan",
      "produce",
    ],
  },
  verbLabel: "B — Behavior: an observable verb",
  verbPlaceholder: "explain",
  verbHint: "Pick a verb for the level, or type your own.",
  contentLabel: "…and what students do it to",
  contentPlaceholder: "the stages of mitosis",
  conditionLabel: "C — Condition: the circumstances",
  conditionPlaceholder: "given a labeled diagram",
  conditionHint:
    "Materials, tools or situation — often starting given, using, after or without.",
  degreeLabel: "D — Degree: how well",
  degreePlaceholder: "with at least 80% accuracy",
  degreeHint:
    "A score, a count, a time limit or a standard, such as in 4 of 5 attempts or within 10 minutes.",
  orderLabel: "Sentence order",
  orders: {
    "condition-first": "Condition first",
    "audience-first": "Audience first",
  },
  phrasingLabel: "Phrasing",
  phrasings: { will: "will", able: "will be able to" },
  resultHeading: "Your objective",
  breakdown: "ABCD breakdown",
  partNames: { a: "Audience", b: "Behavior", c: "Condition", d: "Degree" },
  missing: "—",
  vagueWarning: '"{word}" cannot be observed or measured. Try: {suggestions}.',
  vagueTerms: {
    understand: ["explain", "summarize", "classify", "give examples of"],
    know: ["define", "list", "identify", "recall"],
    learn: ["describe", "use", "demonstrate", "list"],
    appreciate: ["explain the value of", "justify", "evaluate"],
    comprehend: ["explain", "interpret", "paraphrase"],
    grasp: ["explain", "apply", "demonstrate"],
    realize: ["identify", "explain"],
    believe: ["argue", "justify", "defend"],
    internalize: ["apply", "demonstrate", "use"],
    "be aware of": ["identify", "describe", "recognize"],
    "become familiar with": ["identify", "describe", "label"],
    "be familiar with": ["identify", "describe", "label"],
  },
  needBehavior:
    "Choose a verb and say what students will do to see the objective.",
  missingDegree:
    "Add a degree — how well, how many or how fast — so you can tell when the objective is met.",
  missingCondition:
    "Consider adding a condition: the materials, tools or situation students will work with.",
  copy: "Copy objective",
  copied: "Copied!",
  add: "Add to list",
  listHeading: "Objectives for this lesson",
  copyAll: "Copy all",
  remove: "Remove",
  removeLabel: "Remove objective {number}",
  loadExample: "Load example",
  example: {
    audience: "grade 7 students",
    verb: "explain",
    content: "the transfer of energy between trophic levels",
    condition: "given a diagram of a pond ecosystem",
    degree: "in a paragraph that uses at least three key terms correctly",
  },
} as const;

type Strings = typeof LEARNING_OBJECTIVE_GENERATOR_STRINGS;
type Phrasing = keyof Strings["phrasings"];
type Part = "audience" | "verb" | "content" | "condition" | "degree";

interface Saved {
  id: number;
  text: string;
  level: BloomLevel;
}

const EMPTY: Record<Part, string> = {
  audience: "",
  verb: "",
  content: "",
  condition: "",
  degree: "",
};

function Warnings({
  parts,
  strings,
  locale,
}: {
  parts: Record<Part, string>;
  strings: Strings;
  locale: string;
}) {
  const vague = findVagueTerms(
    `${parts.verb} ${parts.content}`,
    Object.keys(strings.vagueTerms),
    locale
  );
  const terms = strings.vagueTerms as Record<string, readonly string[]>;
  const behavior = `${parts.verb}${parts.content}`.trim().length > 0;
  return (
    <div aria-live="polite" className="space-y-2">
      {vague.map((word) => (
        <Notice key={word}>
          {t(strings.vagueWarning, {
            word,
            suggestions: (terms[word] ?? []).join(", "),
          })}
        </Notice>
      ))}
      {behavior ? null : <Notice>{strings.needBehavior}</Notice>}
      {behavior && parts.degree.trim() === "" ? (
        <Notice>{strings.missingDegree}</Notice>
      ) : null}
      {behavior && parts.condition.trim() === "" ? (
        <p className={HINT}>{strings.missingCondition}</p>
      ) : null}
    </div>
  );
}

function Breakdown({
  parts,
  audience,
  strings,
}: {
  parts: Record<Part, string>;
  audience: string;
  strings: Strings;
}) {
  const rows: [string, string][] = [
    [strings.partNames.a, audience],
    [strings.partNames.b, `${parts.verb} ${parts.content}`.trim()],
    [strings.partNames.c, parts.condition.trim()],
    [strings.partNames.d, parts.degree.trim()],
  ];
  return (
    <table className="w-full border-collapse text-left text-sm">
      <tbody>
        {rows.map(([name, value]) => (
          <tr className="align-top" key={name}>
            <th
              className="w-28 py-1 pe-4 font-medium text-foreground"
              scope="row"
            >
              {name}
            </th>
            <td className="py-1 text-muted-foreground">
              {value || strings.missing}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

let nextId = 0;

export default function LearningObjectiveGeneratorWidget() {
  const strings = useToolUi(LEARNING_OBJECTIVE_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const [parts, setParts] = useState<Record<Part, string>>(EMPTY);
  const [level, setLevel] = useState<BloomLevel>("understand");
  const [order, setOrder] = useState<ObjectiveOrder>("condition-first");
  const [phrasing, setPhrasing] = useState<Phrasing>("able");
  const [saved, setSaved] = useState<Saved[]>([]);

  const audience = parts.audience.trim() || strings.audienceFallback;
  const objective = buildObjective(
    { ...parts, audience },
    order,
    strings.phrasings[phrasing],
    locale
  );
  const set = (part: Part) => (value: string) =>
    setParts((previous) => ({ ...previous, [part]: value }));
  const allText = saved
    .map((item, index) => `${index + 1}. ${item.text}`)
    .join("\n");

  function add() {
    nextId += 1;
    setSaved((previous) => [
      ...previous,
      { id: nextId, text: objective, level },
    ]);
  }

  return (
    <div className="space-y-6">
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          setParts({ ...strings.example });
          setLevel("understand");
        }}
        type="button"
      >
        {strings.loadExample}
      </button>
      <TextField
        hint={strings.audienceHint}
        id="lo-audience"
        label={strings.audienceLabel}
        onChange={set("audience")}
        placeholder={strings.audiencePlaceholder}
        value={parts.audience}
      />
      <div className="space-y-2">
        <PillGroup
          label={strings.levelLabel}
          name="lo-level"
          onChange={setLevel}
          options={BLOOM_LEVELS.map((value) => ({
            value,
            label: strings.levelNames[value],
          }))}
          value={level}
        />
        <p className={HINT}>{strings.levelDescriptions[level]}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.verbHint}
          id="lo-verb"
          label={strings.verbLabel}
          onChange={set("verb")}
          placeholder={strings.verbPlaceholder}
          value={parts.verb}
        />
        <TextField
          id="lo-content"
          label={strings.contentLabel}
          onChange={set("content")}
          placeholder={strings.contentPlaceholder}
          value={parts.content}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {strings.levelVerbs[level].map((verb) => (
          <button
            aria-pressed={parts.verb.trim() === verb}
            className={BUTTON_SMALL}
            key={verb}
            onClick={() => set("verb")(verb)}
            type="button"
          >
            {verb}
          </button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.conditionHint}
          id="lo-condition"
          label={strings.conditionLabel}
          onChange={set("condition")}
          placeholder={strings.conditionPlaceholder}
          value={parts.condition}
        />
        <TextField
          hint={strings.degreeHint}
          id="lo-degree"
          label={strings.degreeLabel}
          onChange={set("degree")}
          placeholder={strings.degreePlaceholder}
          value={parts.degree}
        />
      </div>
      <div className="flex flex-wrap gap-6">
        <PillGroup
          label={strings.orderLabel}
          name="lo-order"
          onChange={setOrder}
          options={(["condition-first", "audience-first"] as const).map(
            (value) => ({ value, label: strings.orders[value] })
          )}
          value={order}
        />
        <PillGroup
          label={strings.phrasingLabel}
          name="lo-phrasing"
          onChange={setPhrasing}
          options={(["able", "will"] as const).map((value) => ({
            value,
            label: strings.phrasings[value],
          }))}
          value={phrasing}
        />
      </div>

      <section className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.resultHeading}</h2>
        <p
          aria-live="polite"
          className="text-foreground text-lg leading-relaxed"
        >
          {objective || strings.missing}
        </p>
        <Warnings locale={locale} parts={parts} strings={strings} />
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.breakdown}</h3>
          <Breakdown audience={audience} parts={parts} strings={strings} />
        </div>
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copy}
            text={objective}
          />
          <button
            className={BUTTON_PRIMARY}
            disabled={objective.length === 0}
            onClick={add}
            type="button"
          >
            {strings.add}
          </button>
        </div>
      </section>

      {saved.length > 0 ? (
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className={WIDGET_H2}>{strings.listHeading}</h2>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyAll}
              small
              text={allText}
            />
          </div>
          <ol className="space-y-2">
            {saved.map((item, index) => (
              <li
                className="flex items-start gap-3 rounded-lg border border-border bg-card p-3"
                key={item.id}
              >
                <span className="font-semibold text-foreground text-sm">
                  {index + 1}.
                </span>
                <span className="flex-1 text-foreground text-sm">
                  {item.text}{" "}
                  <span className={LABEL}>
                    ({strings.levelNames[item.level]})
                  </span>
                </span>
                <button
                  aria-label={t(strings.removeLabel, { number: index + 1 })}
                  className={BUTTON_SMALL}
                  onClick={() =>
                    setSaved((previous) =>
                      previous.filter((other) => other.id !== item.id)
                    )
                  }
                  type="button"
                >
                  {strings.remove}
                </button>
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </div>
  );
}
